import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, ArrowRight, Copy, ExternalLink, Loader2, Check, Video, RefreshCw, Edit2, Save, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import nexisLogo from "@/assets/nexis-logo-new.png";
import { useCredits } from "@/hooks/useCredits";
import { NexisBreadcrumb } from "@/components/nexis/NexisBreadcrumb";

interface Scene {
  id: string;
  scene_number: number;
  duration: string;
  description: string;
  image_prompt: string;
}

interface Project {
  id: string;
  title: string;
  script: string | null;
  recommended_model: string | null;
  secondary_model: string | null;
  visual_style: string | null;
  visual_style_category: string | null;
  platform: string | null;
}

export default function NexisImagePrompts() {
  const navigate = useNavigate();
  const { projectId } = useParams();
  const { consume } = useCredits();
  
  const [project, setProject] = useState<Project | null>(null);
  const [scenes, setScenes] = useState<Scene[]>([]);
  const [loading, setLoading] = useState(true);
  const [converting, setConverting] = useState(false);
  const [copiedId, setCopiedId] = useState<string | null>(null);
  const [regeneratingId, setRegeneratingId] = useState<string | null>(null);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editData, setEditData] = useState<{ description: string; prompt: string }>({ description: "", prompt: "" });

  useEffect(() => {
    if (projectId) {
      fetchData();
    }
  }, [projectId]);

  const fetchData = async () => {
    try {
      const [projectRes, scenesRes] = await Promise.all([
        supabase
          .from("nexis_projects")
          .select("id, title, script, recommended_model, secondary_model, visual_style, visual_style_category, platform")
          .eq("id", projectId)
          .single(),
        supabase
          .from("nexis_scenes")
          .select("*")
          .eq("project_id", projectId)
          .order("scene_number", { ascending: true }),
      ]);

      if (projectRes.error) throw projectRes.error;
      if (scenesRes.error) throw scenesRes.error;
      
      setProject(projectRes.data);
      setScenes(scenesRes.data || []);
    } catch (error) {
      console.error("Error fetching data:", error);
      toast.error("Failed to load prompts");
      navigate("/app/nexis-ai");
    } finally {
      setLoading(false);
    }
  };

  const handleCopyPrompt = async (id: string, prompt: string) => {
    await navigator.clipboard.writeText(prompt);
    setCopiedId(id);
    setTimeout(() => setCopiedId(null), 2000);
    toast.success("Prompt copied!");
  };

  const handleCopyAll = async () => {
    const allPrompts = scenes.map((s) => `Scene ${s.scene_number}:\n${s.image_prompt}`).join("\n\n");
    await navigator.clipboard.writeText(allPrompts);
    toast.success("All prompts copied!");
  };

  const handleRegenerateScene = async (scene: Scene) => {
    if (!project) return;
    
    setRegeneratingId(scene.id);
    try {
      const credited = await consume("generate-image-prompts", "Regenerate scene prompt");
      if (!credited) { setRegeneratingId(null); return; }
      const { data, error } = await supabase.functions.invoke("nexis-generate", {
        body: {
          type: "regenerate-scene",
          sceneNumber: scene.scene_number,
          currentDescription: scene.description,
          script: project.script,
          platform: project.platform,
          visualStyle: project.visual_style,
          styleCategory: project.visual_style_category,
        },
      });

      if (error) throw error;
      if (data.error) throw new Error(data.error);

      // Update scene in database
      const { error: updateError } = await supabase
        .from("nexis_scenes")
        .update({
          description: data.result.description,
          image_prompt: data.result.prompt,
        })
        .eq("id", scene.id);

      if (updateError) throw updateError;

      // Update local state
      setScenes((prev) =>
        prev.map((s) =>
          s.id === scene.id
            ? { ...s, description: data.result.description, image_prompt: data.result.prompt }
            : s
        )
      );

      toast.success(`Scene ${scene.scene_number} regenerated!`);
    } catch (error: any) {
      console.error("Error regenerating scene:", error);
      toast.error(error.message || "Failed to regenerate scene");
    } finally {
      setRegeneratingId(null);
    }
  };

  const handleStartEdit = (scene: Scene) => {
    setEditingId(scene.id);
    setEditData({ description: scene.description, prompt: scene.image_prompt });
  };

  const handleCancelEdit = () => {
    setEditingId(null);
    setEditData({ description: "", prompt: "" });
  };

  const handleSaveEdit = async (sceneId: string) => {
    try {
      const { error } = await supabase
        .from("nexis_scenes")
        .update({
          description: editData.description,
          image_prompt: editData.prompt,
        })
        .eq("id", sceneId);

      if (error) throw error;

      // Update local state
      setScenes((prev) =>
        prev.map((s) =>
          s.id === sceneId
            ? { ...s, description: editData.description, image_prompt: editData.prompt }
            : s
        )
      );

      setEditingId(null);
      toast.success("Scene updated!");
    } catch (error) {
      console.error("Error saving scene:", error);
      toast.error("Failed to save changes");
    }
  };

  const handleConvertToVideo = async () => {
    if (!project) return;
    
    setConverting(true);
    try {
      const { data, error } = await supabase.functions.invoke("nexis-generate", {
        body: {
          type: "generate-video-prompts",
          scenes: scenes.map((s) => ({
            sceneNumber: s.scene_number,
            duration: s.duration,
            description: s.description,
            prompt: s.image_prompt,
          })),
          visualStyle: project.visual_style,
        },
      });

      if (error) throw error;
      if (data.error) throw new Error(data.error);

      // Update scenes with video prompts
      for (const videoScene of data.result) {
        await supabase
          .from("nexis_scenes")
          .update({ video_prompt: videoScene.prompt })
          .eq("project_id", projectId)
          .eq("scene_number", videoScene.sceneNumber);
      }

      // Update project step
      await supabase
        .from("nexis_projects")
        .update({ last_step: "video-prompts" })
        .eq("id", projectId);

      toast.success("Video prompts generated!");
      navigate(`/app/nexis-ai/project/${projectId}/video-prompts`);
    } catch (error: any) {
      console.error("Error converting to video:", error);
      toast.error(error.message || "Failed to convert to video prompts");
    } finally {
      setConverting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="p-4 md:p-6 lg:p-8 min-h-screen">
      <div className="max-w-4xl mx-auto">
        <NexisBreadcrumb projectTitle={project?.title} currentStep="Image Prompts" />
        {/* Header */}
        <div className="flex items-center gap-2 md:gap-4 mb-6 md:mb-8">
          <img src={nexisLogo} alt="Nexis AI" className="w-8 h-8 md:w-10 md:h-10 object-contain flex-shrink-0" />
          <div className="flex-1 min-w-0">
            <h1 className="font-display text-lg md:text-2xl font-bold">Image Prompts</h1>
            <p className="text-xs md:text-sm text-muted-foreground truncate">{project?.title}</p>
          </div>
          <Button variant="outline" onClick={handleCopyAll} size="sm" className="text-xs md:text-sm flex-shrink-0">
            <Copy className="w-3 h-3 md:w-4 md:h-4 mr-1.5 md:mr-2" />
            <span className="hidden sm:inline">Copy All</span>
            <span className="sm:hidden">All</span>
          </Button>
        </div>

        {/* Recommended Model */}
        {project?.recommended_model && (
          <Card className="p-4 md:p-6 bg-gradient-to-r from-primary/10 to-purple-500/10 border-primary/20 mb-4 md:mb-6">
            <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-3">
              <div className="min-w-0">
                <p className="text-xs md:text-sm text-muted-foreground mb-1">Recommended OpenArt Model</p>
                <h3 className="text-lg md:text-xl font-bold text-primary">{project.recommended_model}</h3>
                {project.secondary_model && (
                  <p className="text-xs md:text-sm text-muted-foreground mt-1 md:mt-2">
                    Alternative: <span className="text-foreground">{project.secondary_model}</span>
                  </p>
                )}
              </div>
              <Button variant="outline" size="sm" asChild className="text-xs md:text-sm w-full sm:w-auto flex-shrink-0">
                <a href="https://openart.ai/home/?via=fint" target="_blank" rel="noopener noreferrer">
                  <ExternalLink className="w-3 h-3 md:w-4 md:h-4 mr-1.5 md:mr-2" />
                  Open in OpenArt
                </a>
              </Button>
            </div>
          </Card>
        )}

        {/* Scene Cards */}
        <div className="space-y-3 md:space-y-4 mb-6 md:mb-8">
          {scenes.map((scene) => (
            <Card key={scene.id} className="p-3 md:p-5 bg-card/50 border-border/50">
              <div className="flex items-start justify-between mb-2 md:mb-3">
                <div className="flex items-center gap-2 md:gap-3">
                  <Badge variant="outline" className="text-xs md:text-sm">
                    Scene {scene.scene_number}
                  </Badge>
                  <span className="text-xs md:text-sm text-muted-foreground">{scene.duration}</span>
                </div>
                <div className="flex items-center gap-1">
                  {editingId === scene.id ? (
                    <>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={handleCancelEdit}
                      >
                        <X className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleSaveEdit(scene.id)}
                        className="text-green-500 hover:text-green-600"
                      >
                        <Save className="w-4 h-4" />
                      </Button>
                    </>
                  ) : (
                    <>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleRegenerateScene(scene)}
                        disabled={regeneratingId === scene.id}
                        title="Regenerate this scene"
                      >
                        {regeneratingId === scene.id ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          <RefreshCw className="w-4 h-4" />
                        )}
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleStartEdit(scene)}
                        title="Edit this scene"
                      >
                        <Edit2 className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleCopyPrompt(scene.id, scene.image_prompt)}
                        title="Copy prompt"
                      >
                        {copiedId === scene.id ? (
                          <Check className="w-4 h-4 text-green-500" />
                        ) : (
                          <Copy className="w-4 h-4" />
                        )}
                      </Button>
                    </>
                  )}
                </div>
              </div>
              
              {editingId === scene.id ? (
                <div className="space-y-2 md:space-y-3">
                  <div>
                    <label className="text-[10px] md:text-xs text-muted-foreground mb-1 block">Description</label>
                    <Input
                      value={editData.description}
                      onChange={(e) => setEditData((prev) => ({ ...prev, description: e.target.value }))}
                      placeholder="Scene description..."
                      className="text-sm"
                    />
                  </div>
                  <div>
                    <label className="text-[10px] md:text-xs text-muted-foreground mb-1 block">Image Prompt</label>
                    <Textarea
                      value={editData.prompt}
                      onChange={(e) => setEditData((prev) => ({ ...prev, prompt: e.target.value }))}
                      className="min-h-[80px] md:min-h-[100px] font-mono text-xs md:text-sm"
                      placeholder="Image generation prompt..."
                    />
                  </div>
                </div>
              ) : (
                <>
                  <p className="text-xs md:text-sm text-muted-foreground mb-2 md:mb-3">{scene.description}</p>
                  <div className="bg-background/50 rounded-lg p-2 md:p-3">
                    <p className="text-xs md:text-sm font-mono break-words">{scene.image_prompt}</p>
                  </div>
                </>
              )}
            </Card>
          ))}
        </div>

        {/* Convert to Video Button */}
        <Button
          variant="hero"
          size="default"
          className="w-full gap-2 text-sm md:text-base"
          onClick={handleConvertToVideo}
          disabled={converting || editingId !== null}
        >
          {converting ? (
            <>
              <Loader2 className="w-4 h-4 md:w-5 md:h-5 animate-spin" />
              <span className="hidden sm:inline">Converting to Video Prompts...</span>
              <span className="sm:hidden">Converting...</span>
            </>
          ) : (
            <>
              <Video className="w-4 h-4 md:w-5 md:h-5" />
              <span className="hidden sm:inline">Convert All to Video Prompts</span>
              <span className="sm:hidden">Convert to Video</span>
              <ArrowRight className="w-4 h-4 md:w-5 md:h-5" />
            </>
          )}
        </Button>
      </div>
    </div>
  );
}
